import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
  Alert,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Download, FilterList, Clear, Delete } from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';

interface File {
  id: string;
  filename: string;
  real_filename: string;
  download_url: string;
  created: string;
}

interface FilesResponse {
  files: File[];
  total_count: number;
}

const Files: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    file_name: '',
    file_format: [] as string[],
    min_date: '',
    max_date: '',
  });

  // УДАЛЕНИЕ
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; file?: File }>({ open: false });

  const auth = useContext(AuthContext);
  const TIMEOUT = 10000;

  const fetchFiles = async (pageNum: number = page) => {
    if (!auth?.isAuthenticated) {
      setMessage({ text: 'Авторизуйтесь для просмотра файлов', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post<FilesResponse>(
        '/files',
        {
          file_name: filters.file_name || null,
          file_format: filters.file_format.length ? filters.file_format : null,
          min_date: filters.min_date || null,
          max_date: filters.max_date || null,
          offset: (pageNum - 1) * limit,
          limit,
        },
        { timeout: TIMEOUT }
      );

      setFiles(response.data.files || []);
      setTotalCount(response.data.total_count || 0);
      setMessage(null);
    } catch (error: any) {
      console.error('Ошибка загрузки файлов:', error);
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isAuthenticated]);

  useEffect(() => {
    fetchFiles(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
    fetchFiles(value);
  };

  const downloadFile = (url: string, filename: string) => {
  // Просто создаём ссылку и кликаем — браузер сам скачает
  const link = document.createElement('a');
  link.href = url;
  link.download = filename; // важно! заставляет скачать, а не открыть
  link.target = '_blank';   // на всякий случай
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setMessage({ text: 'Скачивание начато...', type: 'success' });
};

  const resetFilters = () => {
    setFilters({
      file_name: '',
      file_format: [],
      min_date: '',
      max_date: '',
    });
    setPage(1);
  };

  // УДАЛЕНИЕ
  const confirmDelete = (file: File) => {
    setDeleteDialog({ open: true, file });
  };

  const handleDelete = async () => {
    if (!deleteDialog.file) return;

    try {
      await api.delete(`/files/${deleteDialog.file.id}`, { timeout: TIMEOUT });
      setMessage({ text: 'Файл удалён', type: 'success' });
      setDeleteDialog({ open: false });
      fetchFiles(page);
    } catch (error: any) {
      setMessage({ text: `Ошибка удаления: ${error.response?.data?.detail || error.message}`, type: 'error' });
      setDeleteDialog({ open: false });
    }
  };

  const gridItem = { display: 'flex', alignItems: 'center', width: '100%' };

  if (isLoading && files.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ПОДРОБНОЕ ОПИСАНИЕ — НЕЙРОКОММЕНТИНГ === */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Выгрузки: результаты парсинга и аналитические отчеты
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Здесь хранятся все файлы, сгенерированные при выполнении задач парсинга. Скачивайте их в нужном формате: XLSX, CSV, Parquet, JSON.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Форматы</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • <strong>XLSX</strong> — Excel<br />
              • <strong>CSV</strong> — таблицы<br />
              • <strong>Parquet</strong> — big data<br />
              • <strong>JSON</strong> — API
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Фильтры</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • По имени файла<br />
              • По формату<br />
              • По дате создания
            </Typography>
          </Box>
        </Box>

        <Box sx={{ bgcolor: '#EBF8FF', p: 3, borderRadius: 2, borderLeft: '4px solid #1E40AF' }}>
          <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Совет:</Typography>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
            Используйте <strong>фильтры</strong> для быстрого поиска нужного файла.
          </Typography>
        </Box>

        <Typography sx={{ color: '#4B5563', mt: 3, fontStyle: 'italic' }}>
          Файлы хранятся неограниченное количество времени - вы всегда можете получить к ним доступ!
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* === ФИЛЬТРЫ === */}
      <Paper elevation={2} sx={{ borderRadius: 2, p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ color: '#1E40AF', mr: 1 }} />
          <Typography variant="h6" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Фильтры
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={gridItem}>
            <TextField
              label="Имя файла"
              value={filters.file_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, file_name: e.target.value }))}
              fullWidth
              size="small"
              placeholder="parsing_2025"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={gridItem}>
            <FormControl fullWidth size="small">
              <InputLabel>Формат</InputLabel>
              <Select
                multiple
                value={filters.file_format}
                onChange={(e) => setFilters((prev) => ({ ...prev, file_format: e.target.value as string[] }))}
                label="Формат"
              >
                <MenuItem value="xlsx">XLSX</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="parquet">Parquet</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={gridItem}>
            <TextField
              label="От даты"
              type="date"
              value={filters.min_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, min_date: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={gridItem}>
            <TextField
              label="До даты"
              type="date"
              value={filters.max_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, max_date: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" startIcon={<Clear />} onClick={resetFilters} sx={{ mr: 1 }}>
              Сбросить
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* === СПИСОК ФАЙЛОВ === */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Найдено файлов: {totalCount}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {files.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Имя файла</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Формат</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Создан</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => {
                    const format = file.filename.split('.').pop()?.toUpperCase() || '—';
                    return (
                      <TableRow key={file.id} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                        <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {file.filename}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            {(() => {
                              const match = file.real_filename.match(/\.([^.]+)$/);
                              return match ? match[1].toUpperCase() : '—';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.875rem', color: '#4B5563' }}>
                            {new Date(file.created).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => downloadFile(file.download_url, file.filename)}
                            disabled={isLoading}
                            sx={{
                              color: '#10B981',
                              '&:hover': { bgcolor: '#ECFDF5' },
                              mr: 1,
                            }}
                            title="Скачать"
                          >
                            <Download fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => confirmDelete(file)}
                            sx={{
                              color: '#EF4444',
                              '&:hover': { bgcolor: '#FEF2F2' },
                            }}
                            title="Удалить"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Файлы не найдены. Попробуйте изменить фильтры.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ДИАЛОГ УДАЛЕНИЯ */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>Удалить файл?</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить файл <strong>{deleteDialog.file?.filename}</strong>?
          </Typography>
          <Typography sx={{ mt: 1, fontSize: '0.875rem', color: '#6B7280' }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Files;